import type {
  BedHistoryDto,
  GetBedHistoryRequestDto,
} from '@/shared/api/modules/floor-plan';
import { useTableSortingState } from '@/shared/lib/hooks';
import { Table, Tag } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import capitalize from 'lodash.capitalize';
import { useState, type FC } from 'react';
import { getBedHistoryQueryOptions } from '../api/get-bed-history.query';
import { FloorPlanHistoryStatus } from '../model/contracts';
import { BedHistoryAlerts } from './bed-history-alerts';

const columnHelper = createColumnHelper<BedHistoryDto>();

/** MM/DD/YYYY of the UTC calendar day (the API sends UTC midnight; local time would shift it). */
const formatUtcDay = (date: string) =>
  new Date(date).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

const columns = [
  columnHelper.accessor('residentName', {
    enableSorting: true,
    header: 'Resident',
    maxSize: 200,
    cell: ({ getValue }) => getValue() ?? 'N/A',
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('checkInDate', {
    enableSorting: true,
    header: 'Check-In Date',
    cell: ({ getValue }) => {
      const date = getValue();
      if (!date) return 'N/A';
      return formatUtcDay(date);
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('checkOutDate', {
    enableSorting: true,
    header: 'Check-Out Date',
    maxSize: 200,
    cell: ({ getValue }) => {
      const date = getValue();
      if (!date) return '-';
      return formatUtcDay(date);
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('status', {
    enableSorting: true,
    header: 'Status',
    cell: ({ getValue }) => (
      <Tag
        text={capitalize(getValue().toLowerCase())}
        variant={getValue() === FloorPlanHistoryStatus.ACTIVE ? 'green' : 'red'}
      />
    ),
    footer: (info) => info.column.id,
  }),
  columnHelper.display({
    id: 'alerts',
    header: '',
    enableSorting: false,
    maxSize: 48,
    cell: ({ row }) => <BedHistoryAlerts alerts={row.original.alerts} />,
  }),
] as ColumnDef<BedHistoryDto>[];

type FloorPlanHistorySubTableProps = {
  floorPlanId: number;
};

const PER_PAGE = 10;

export const FloorPlanHistorySubTable: FC<FloorPlanHistorySubTableProps> = ({
  floorPlanId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { sortingState, setSortingState, sortBy, sortDirection } =
    useTableSortingState<NonNullable<GetBedHistoryRequestDto['sortBy']>>({
      sortBy: 'checkInDate',
      sortDirection: 'desc',
    });

  const {
    data: bedHistoryResponse,
    isPending,
    isError,
  } = useQuery(
    getBedHistoryQueryOptions({
      floorPlanId,
      page: currentPage,
      perPage: PER_PAGE,
      sortBy,
      sortDirection,
    })
  );

  const bedHistoryData = bedHistoryResponse?.data ?? [];
  const totalPages = bedHistoryResponse?.meta?.totalPages ?? 1;

  return (
    <Table
      striped
      columns={columns}
      errored={isError}
      data={bedHistoryData}
      sortingState={sortingState}
      emptyState={!bedHistoryData.length && !isPending}
      pagination={{
        currentPage,
        totalPages,
        onPageChange: (page) => setCurrentPage(page),
      }}
      loading={isPending}
      onSortingChange={setSortingState}
    />
  );
};
